/**
 * Contatos de um dono (pessoa/empresa/condomínio): telefones, e-mails e redes sociais. Cada tipo
 * é um cartão com listar / adicionar / desvincular. Endereços têm painel próprio (`PainelEnderecos`),
 * por causa do CEP/geocodificação e do vínculo temporal.
 *
 * O princípio que atravessa os três: os recursos são **deduplicados** no banco (telefone por `raw`,
 * e-mail por endereço, rede por plataforma+identificador). Somar um contato que já existe não cria
 * outro — reusa o recurso e cria só o **vínculo** (classificação + principal), enriquecendo o grafo.
 * Desvincular remove o vínculo, não o recurso (ele pode ser de mais gente). O selo de correlação
 * mostra na hora quem mais compartilha aquele recurso — a conexão viva, no lugar onde o dado vive.
 */
import { AtSign, Pencil, Phone, Share2, Star, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CampoTelefone } from '@/components/CampoTelefone';
import { BadgeCorrelacao } from '@/components/crud/BadgeCorrelacao';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Chip, Spinner } from '@/components/ui/misc';
import {
  api,
  type ContatoEmailCad,
  type ContatoRedeCad,
  type ContatoTelefoneCad,
} from '@/lib/api';
import { useCorrelacoes } from '@/lib/correlacoes';
import { formatarTelefone } from '@/lib/telefone';
import type { OwnerTipo } from '@/lib/recursos/tipos';

const CLASSIF_TELEFONE = ['pessoal', 'comercial', 'residencial', 'recado', 'whatsapp', 'fax'];
const CLASSIF_EMAIL = ['pessoal', 'comercial', 'financeiro', 'nfe', 'suporte'];
const CLASSIF_REDE = ['pessoal', 'comercial', 'institucional'];
const PLATAFORMAS = ['instagram', 'facebook', 'linkedin', 'x', 'tiktok', 'youtube', 'whatsapp', 'site', 'outro'];

export function PainelContatos({ ownerTipo, ownerId }: { ownerTipo: OwnerTipo; ownerId: string }) {
  const base = `/cadastro/${ownerTipo}/${ownerId}`;
  return (
    <>
      <SecaoTelefones base={base} />
      <SecaoEmails base={base} />
      <SecaoRedes base={base} />
    </>
  );
}

/** Cabeçalho + moldura comum a todos os cartões de contato. */
function Cartao({
  icone: Icone,
  titulo,
  children,
}: {
  icone: typeof Phone;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icone className="size-4 text-accent" />
          {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  );
}

function SelectClassif({
  valor,
  onChange,
  opcoes,
}: {
  valor: string;
  onChange: (v: string) => void;
  opcoes: string[];
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="tipo-rotulo">Classificação</span>
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
      >
        {opcoes.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </label>
  );
}

function CheckPrincipal({ valor, onChange }: { valor: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex h-10 cursor-pointer items-center gap-2">
      <input
        type="checkbox"
        checked={valor}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 accent-[var(--primary)]"
      />
      <span className="text-[15px] text-text">Principal</span>
    </label>
  );
}

function LinhaContato({
  children,
  classificacao,
  principal,
  selo,
  opcoesClassif,
  onReclassificar,
  onRemover,
}: {
  children: React.ReactNode;
  classificacao: string;
  principal: boolean;
  selo?: React.ReactNode;
  opcoesClassif: string[];
  /** Salva a nova etiqueta/principal; devolve `true` no sucesso (fecha a edição). */
  onReclassificar: (novaClassif: string, principal: boolean) => Promise<boolean>;
  onRemover: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [classif, setClassif] = useState(classificacao);
  const [princ, setPrinc] = useState(principal);
  const [salvando, setSalvando] = useState(false);

  function abrir() {
    setClassif(classificacao);
    setPrinc(principal);
    setEditando(true);
  }

  async function salvar() {
    if (salvando) return;
    setSalvando(true);
    const ok = await onReclassificar(classif, princ);
    setSalvando(false);
    if (ok) setEditando(false);
  }

  if (editando) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border-strong bg-surface-2 px-3 py-2">
        <div className="min-w-0 flex-1 tipo-dado text-text-tertiary">{children}</div>
        <select
          value={classif}
          onChange={(e) => setClassif(e.target.value)}
          className="h-9 rounded-md border border-border bg-surface px-2 text-[14px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
        >
          {opcoesClassif.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="flex cursor-pointer items-center gap-1.5 text-[14px] text-text">
          <input
            type="checkbox"
            checked={princ}
            onChange={(e) => setPrinc(e.target.checked)}
            className="size-4 accent-[var(--primary)]"
          />
          principal
        </label>
        <Button variant="secundario" size="sm" onClick={() => void salvar()} disabled={salvando}>
          {salvando ? <Spinner /> : 'Salvar'}
        </Button>
        <Button variant="fantasma" size="sm" onClick={() => setEditando(false)} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1">{children}</div>
      {selo}
      <Chip>{classificacao}</Chip>
      {principal && (
        <Chip aceso>
          <Star className="mr-1 size-3" /> principal
        </Chip>
      )}
      <Button variant="fantasma" size="sm" onClick={abrir} aria-label="Editar classificação">
        <Pencil />
      </Button>
      <Button
        variant="fantasma"
        size="sm"
        className="text-danger hover:text-danger"
        onClick={onRemover}
        aria-label="Desvincular"
      >
        <Trash2 />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------- Telefones
function SecaoTelefones({ base }: { base: string }) {
  const url = `${base}/telefones`;
  const [itens, setItens] = useState<ContatoTelefoneCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [numero, setNumero] = useState('');
  const [classificacao, setClassificacao] = useState('pessoal');
  const [principal, setPrincipal] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const correl = useCorrelacoes(
    'telefone',
    itens.map((t) => t.telefone.id),
  );

  async function carregar() {
    const r = await api.get<ContatoTelefoneCad[]>(url);
    setCarregando(false);
    if (r.ok) setItens(r.dados);
  }
  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setNumero('');
    setPrincipal(false);
    setWhatsapp(false);
    setClassificacao('pessoal');
  }

  async function adicionar(): Promise<boolean> {
    if (!numero.trim() || salvando) return false;
    setSalvando(true);
    const r = await api.post(url, {
      telefone: { numero: numero.trim(), is_whatsapp: whatsapp },
      classificacao,
      principal,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Telefone vinculado. Se já existia, apenas o correlacionamos.');
    limparForm();
    void carregar();
    return true;
  }

  async function remover(t: ContatoTelefoneCad) {
    const r = await api.del(`${url}/${t.telefone.id}/${t.classificacao}`);
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  async function reclassificar(
    telefoneId: string,
    classifAtual: string,
    nova: string,
    principal: boolean,
  ): Promise<boolean> {
    const r = await api.patch(`${url}/${telefoneId}/${classifAtual}`, {
      nova_classificacao: nova,
      principal,
    });
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Classificação atualizada.');
    void carregar();
    return true;
  }

  return (
    <Cartao icone={Phone} titulo="Telefones">
      {estadoDaLista(carregando, itens.length === 0, 'Nenhum telefone vinculado.') ?? (
        <div className="flex flex-col gap-2">
          {itens.map((t) => (
            <LinhaContato
              key={`${t.telefone.id}-${t.classificacao}`}
              classificacao={t.classificacao}
              principal={t.principal}
              selo={<BadgeCorrelacao correlacao={correl[t.telefone.id]} />}
              opcoesClassif={CLASSIF_TELEFONE}
              onReclassificar={(nova, princ) => reclassificar(t.telefone.id, t.classificacao, nova, princ)}
              onRemover={() => void remover(t)}
            >
              <span className="tipo-dado text-text">
                {formatarTelefone(t.telefone.telefone || t.telefone.raw)}
              </span>
              {t.telefone.is_whatsapp && (
                <Chip className="ml-2 border-success/40 text-success">WhatsApp</Chip>
              )}
            </LinhaContato>
          ))}
        </div>
      )}
      <DrawerAdicionar
        titulo="Novo telefone"
        descricao="Se o número já existir na base, apenas o correlacionamos — nada é duplicado."
        rotuloAbrir="Adicionar telefone"
        podeSalvar={!!numero.trim()}
        salvando={salvando}
        onSalvar={adicionar}
        onFechar={limparForm}
      >
        <CampoTelefone rotulo="Telefone" valor={numero} onChange={setNumero} />
        <SelectClassif valor={classificacao} onChange={setClassificacao} opcoes={CLASSIF_TELEFONE} />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex h-10 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={whatsapp}
              onChange={(e) => setWhatsapp(e.target.checked)}
              className="size-4 accent-[var(--primary)]"
            />
            <span className="text-[15px] text-text">WhatsApp</span>
          </label>
          <CheckPrincipal valor={principal} onChange={setPrincipal} />
        </div>
      </DrawerAdicionar>
    </Cartao>
  );
}

// ---------------------------------------------------------------- E-mails
function SecaoEmails({ base }: { base: string }) {
  const url = `${base}/emails`;
  const [itens, setItens] = useState<ContatoEmailCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [endereco, setEndereco] = useState('');
  const [classificacao, setClassificacao] = useState('pessoal');
  const [principal, setPrincipal] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const correl = useCorrelacoes(
    'email',
    itens.map((m) => m.email.id),
  );

  async function carregar() {
    const r = await api.get<ContatoEmailCad[]>(url);
    setCarregando(false);
    if (r.ok) setItens(r.dados);
  }
  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setEndereco('');
    setPrincipal(false);
    setClassificacao('pessoal');
  }

  async function adicionar(): Promise<boolean> {
    if (!endereco.trim() || salvando) return false;
    setSalvando(true);
    const r = await api.post(url, { email: { endereco: endereco.trim() }, classificacao, principal });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('E-mail vinculado.');
    limparForm();
    void carregar();
    return true;
  }

  async function remover(m: ContatoEmailCad) {
    const r = await api.del(`${url}/${m.email.id}/${m.classificacao}`);
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  async function reclassificar(
    emailId: string,
    classifAtual: string,
    nova: string,
    principal: boolean,
  ): Promise<boolean> {
    const r = await api.patch(`${url}/${emailId}/${classifAtual}`, {
      nova_classificacao: nova,
      principal,
    });
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Classificação atualizada.');
    void carregar();
    return true;
  }

  return (
    <Cartao icone={AtSign} titulo="E-mails">
      {estadoDaLista(carregando, itens.length === 0, 'Nenhum e-mail vinculado.') ?? (
        <div className="flex flex-col gap-2">
          {itens.map((m) => (
            <LinhaContato
              key={`${m.email.id}-${m.classificacao}`}
              classificacao={m.classificacao}
              principal={m.principal}
              selo={<BadgeCorrelacao correlacao={correl[m.email.id]} />}
              opcoesClassif={CLASSIF_EMAIL}
              onReclassificar={(nova, princ) => reclassificar(m.email.id, m.classificacao, nova, princ)}
              onRemover={() => void remover(m)}
            >
              <span className="tipo-dado text-text">{m.email.endereco}</span>
            </LinhaContato>
          ))}
        </div>
      )}
      <DrawerAdicionar
        titulo="Novo e-mail"
        descricao="Se o e-mail já existir na base, apenas o correlacionamos — nada é duplicado."
        rotuloAbrir="Adicionar e-mail"
        podeSalvar={!!endereco.trim()}
        salvando={salvando}
        onSalvar={adicionar}
        onFechar={limparForm}
      >
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">E-mail</span>
          <Input
            type="email"
            value={endereco}
            onChange={(e) => setEndereco(e.target.value)}
            placeholder="nome@dominio.com"
            className="font-mono"
          />
        </label>
        <SelectClassif valor={classificacao} onChange={setClassificacao} opcoes={CLASSIF_EMAIL} />
        <CheckPrincipal valor={principal} onChange={setPrincipal} />
      </DrawerAdicionar>
    </Cartao>
  );
}

// ---------------------------------------------------------------- Redes sociais
function SecaoRedes({ base }: { base: string }) {
  const url = `${base}/redes`;
  const [itens, setItens] = useState<ContatoRedeCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [plataforma, setPlataforma] = useState('instagram');
  const [identificador, setIdentificador] = useState('');
  const [classificacao, setClassificacao] = useState('pessoal');
  const [principal, setPrincipal] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const correl = useCorrelacoes(
    'rede',
    itens.map((rd) => rd.rede_social.id),
  );

  async function carregar() {
    const r = await api.get<ContatoRedeCad[]>(url);
    setCarregando(false);
    if (r.ok) setItens(r.dados);
  }
  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setIdentificador('');
    setPrincipal(false);
    setPlataforma('instagram');
    setClassificacao('pessoal');
  }

  async function adicionar(): Promise<boolean> {
    if (!identificador.trim() || salvando) return false;
    setSalvando(true);
    const r = await api.post(url, {
      rede_social: { plataforma, identificador: identificador.trim() },
      classificacao,
      principal,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Rede social vinculada.');
    limparForm();
    void carregar();
    return true;
  }

  async function remover(rd: ContatoRedeCad) {
    const r = await api.del(`${url}/${rd.rede_social.id}/${rd.classificacao}`);
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  async function reclassificar(
    redeId: string,
    classifAtual: string,
    nova: string,
    principal: boolean,
  ): Promise<boolean> {
    const r = await api.patch(`${url}/${redeId}/${classifAtual}`, {
      nova_classificacao: nova,
      principal,
    });
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Classificação atualizada.');
    void carregar();
    return true;
  }

  return (
    <Cartao icone={Share2} titulo="Redes sociais">
      {estadoDaLista(carregando, itens.length === 0, 'Nenhuma rede vinculada.') ?? (
        <div className="flex flex-col gap-2">
          {itens.map((rd) => (
            <LinhaContato
              key={`${rd.rede_social.id}-${rd.classificacao}`}
              classificacao={rd.classificacao}
              principal={rd.principal}
              selo={<BadgeCorrelacao correlacao={correl[rd.rede_social.id]} />}
              opcoesClassif={CLASSIF_REDE}
              onReclassificar={(nova, princ) => reclassificar(rd.rede_social.id, rd.classificacao, nova, princ)}
              onRemover={() => void remover(rd)}
            >
              <span className="text-[15px] text-text">
                <span className="text-text-tertiary">{rd.rede_social.plataforma}</span>{' '}
                {rd.rede_social.identificador}
              </span>
            </LinhaContato>
          ))}
        </div>
      )}
      <DrawerAdicionar
        titulo="Nova rede social"
        descricao="Se o perfil já existir na base, apenas o correlacionamos — nada é duplicado."
        rotuloAbrir="Adicionar rede"
        podeSalvar={!!identificador.trim()}
        salvando={salvando}
        onSalvar={adicionar}
        onFechar={limparForm}
      >
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Plataforma</span>
          <select
            value={plataforma}
            onChange={(e) => setPlataforma(e.target.value)}
            className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
          >
            {PLATAFORMAS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Identificador / perfil</span>
          <Input value={identificador} onChange={(e) => setIdentificador(e.target.value)} placeholder="@usuario ou URL" />
        </label>
        <SelectClassif valor={classificacao} onChange={setClassificacao} opcoes={CLASSIF_REDE} />
        <CheckPrincipal valor={principal} onChange={setPrincipal} />
      </DrawerAdicionar>
    </Cartao>
  );
}
